import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {Button} from '@wandb/weave/components/Button';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';
import {twMerge} from 'tailwind-merge';

import {usePanelComments} from '../../state/reports/usePanelComments';
import * as RunHooks from '../../state/runs/hooks';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {isMobile} from '../../util/panelbankFlow';
import {getPanelSpec} from '../../util/panelTypes';
import {useFeatureFlagVegaEnabled} from '../../util/useFeatureFlag';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import {PanelActionsMenu} from './PanelActionsMenu';
import {usePanelContext} from './PanelContextProvider';
import {PanelDragHandle} from './PanelDragHandle';
import {PanelEditButton} from './PanelEditButton';
import {PanelFullScreenButton} from './PanelFullScreenButton';
import {PanelRunMetricButton} from './PanelRunMetricButton';
import {PanelPopupMode} from './types';

export const PanelHeader = ({
  defaultMoveToSectionRef,
  shouldTurnOnComments,
}: {
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  shouldTurnOnComments: boolean;
}) => {
  const {runSetRefs} = usePanelsSharedRefsContext();

  const {
    isReadOnly,
    panelBankSectionConfigRef,
    panelRef,
    panel,
    setPanelPopupMode,
  } = usePanelContext();

  const panelType = panel.viewType;
  const panelSpec = getPanelSpec(panelType);

  const {panelCommentCount, openPanelComments, addComment} = usePanelComments({
    panelRef,
  });

  const {entityName} = RunHooks.useRunSetsQuery({runSetRefs});

  const vegaPanelEnabled = useFeatureFlagVegaEnabled(entityName);

  const [isOverflowMenuActive, setIsOverflowMenuActive] = useState(false);

  return (
    <TailwindContents>
      <div
        className={twMerge(
          'flex shrink-0',
          'mb-4 min-h-[24px]',
          // on a regular workspace OR report where multiple panels are visible, we want to only show
          // the actionable left side items when the mouse is over the panel. it should always be visible in full screen where we only
          // show 1 panel
          'opacity-0 hover:opacity-100',
          // since we hide the actionable items until mouse hover on a regular workspace or report where we display multiple panels,
          // we still want to show the actionable items when overflow menu is active
          isOverflowMenuActive && 'opacity-100'
        )}
        data-test="panel-header">
        {!isReadOnly && <PanelDragHandle panelRef={panelRef} />}
        <div
          className={twMerge(
            // on a regular workspace OR report where multiple panels are visible, the actions should be rendered on top
            // of the drag handle and there's no back button to consider
            'absolute right-8'
          )}>
          <div className={twMerge('flex')}>
            <PanelRunMetricButton
              entityName={entityName}
              panelBankSectionConfigRef={panelBankSectionConfigRef}
              panelRef={panelRef}
              panelType={panelType}
            />
            {/* View spec */}
            {isReadOnly && panelType === 'Vega2' && (
              <Popup
                content="View panel spec"
                size="mini"
                position="top center"
                trigger={
                  <LegacyWBIcon
                    name="show"
                    aria-label="View panel spec"
                    className="panel-action"
                    onClick={() => setPanelPopupMode(PanelPopupMode.VIEW_SPEC)}
                  />
                }
              />
            )}
            {/* View full screen */}
            {!isMobile() && (
              <PanelFullScreenButton
                panelBankSectionConfigRef={panelBankSectionConfigRef}
              />
            )}
            {/* Edit panel */}
            {!isReadOnly &&
              !isMobile() &&
              !panelSpec.noEditMode &&
              (panelType !== 'Vega' || vegaPanelEnabled) && <PanelEditButton />}
            {isReadOnly && shouldTurnOnComments && (
              <Button
                icon="forum-chat-bubble"
                tooltip={'Add a comment'}
                tooltipProps={{className: 'py-8 px-12'}}
                variant="ghost"
                size="small"
                name="chat"
                title="Add comment"
                aria-label="Add comment"
                className="mr-8"
                onClick={addComment}
              />
            )}
            {/* Three-dot popup menu */}
            {!isReadOnly && panelBankSectionConfigRef != null && (
              <PanelActionsMenu
                panelSpec={panelSpec}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                defaultMoveToSectionRef={defaultMoveToSectionRef}
                isOpen={isOverflowMenuActive}
                setIsOpen={setIsOverflowMenuActive}
              />
            )}
            {shouldTurnOnComments &&
              panelCommentCount != null &&
              panelCommentCount > 0 && (
                <Popup
                  content="View comments"
                  inverted
                  size="mini"
                  position="top center"
                  trigger={
                    <div className="panel-comments" onClick={openPanelComments}>
                      <span>{panelCommentCount}</span>
                    </div>
                  }
                />
              )}
          </div>
        </div>
      </div>
    </TailwindContents>
  );
};
